import type { Branding } from '@helios/ui';

type Props = {
  branding: Branding;
};

export function RefreshSmilesLoginLeft({ branding }: Props) {
  const primaryColor = branding.theme?.primaryColor ?? '#2563eb';

  // Brand accents inspired by the clinic's gold logo
  const gold = '#B08A3E';
  const ink = '#1e293b';
  const muted = '#475569';

  const bullets = [
    'Gentle, patient-first care — clear plans and honest guidance',
    'Modern dental-tech ready workflows for a smooth clinic day',
    'Secure access for doctors and staff — your records stay protected',
  ];

  const logoHorizontal =
    branding.orgLogoHorizontalUrl || '/clients/refresh-smiles/logo-horizontal.png';

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        backgroundImage:
          "linear-gradient(160deg, rgba(255,247,237,0.55) 0%, rgba(255,255,255,0.62) 45%, rgba(248,250,252,0.72) 100%), url('/clients/refresh-smiles/hero.png')",
        backgroundSize: 'cover, 135% auto',
        backgroundPosition: 'center, 60% 40%',
        backgroundRepeat: 'no-repeat, no-repeat',
      }}
    >
      {/* Decorative blur shapes */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.14,
            filter: 'blur(60px)',
            backgroundColor: primaryColor,
            width: '22rem',
            height: '22rem',
            top: '-6rem',
            left: '-6rem',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.12,
            filter: 'blur(60px)',
            backgroundColor: gold,
            width: '18rem',
            height: '18rem',
            top: '26%',
            left: '10%',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.1,
            filter: 'blur(70px)',
            backgroundColor: '#f9a8d4',
            width: '520px',
            height: '520px',
            bottom: '-10rem',
            left: '18%',
          }}
        />
      </div>

      {/* Edge fade overlay (softly blends hero image into the background) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 1,
          // Lighten/fade the edges so the background image doesn't look like a hard rectangle
          background: `radial-gradient(ellipse at center, rgba(255,255,255,0) 42%, rgba(255,255,255,0.92) 100%),\
             linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.10) 40%, rgba(255,255,255,0.78) 100%)`,
          // Extra softness on the perimeter
          boxShadow: 'inset 0 0 120px 80px rgba(255,255,255,0.70)',
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          height: '100%',
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr) auto',
          padding: '3.5rem 4.5rem',
          boxSizing: 'border-box',
        }}
      >
        {/* Brand */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <img
            src={logoHorizontal}
            alt={branding.orgName || 'Refresh Smiles Dental Clinic'}
            style={{ width: 'min(560px, 100%)', height: 'auto' }}
          />

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: ink,
                padding: '0.35rem 0.65rem',
                borderRadius: '9999px',
                background: `${gold}1A`,
                border: `1px solid ${gold}33`,
              }}
            >
              Dental Clinic
            </span>
            <span style={{ color: '#94a3b8' }}>•</span>
            <span style={{ fontSize: '0.95rem', color: muted }}>
              Smart, modern dental practice workspace
            </span>
          </div>
        </div>

        {/* Center copy */}
        <div style={{ alignSelf: 'center', maxWidth: '44rem' }}>
          <h1
            style={{
              fontSize: '2.25rem',
              fontWeight: 750,
              color: ink,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
            }}
          >
            Welcome Back Dr. Reena.
            <br />
            <span style={{ color: primaryColor }}>Let’s refresh a smile today.</span>
          </h1>

          <p style={{ marginTop: '0.9rem', fontSize: '1.08rem', color: muted, maxWidth: '40rem' }}>
            Designed for fast check-ins, organized patient records, and a calm, professional
            experience — from consults to cosmetic treatments.
          </p>

          <ul
            style={{
              marginTop: '1.75rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.9rem',
            }}
          >
            {bullets.map((text) => (
              <li
                key={text}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.85rem',
                  color: muted,
                }}
              >
                <span
                  style={{
                    marginTop: '0.25rem',
                    flexShrink: 0,
                    width: '1.25rem',
                    height: '1.25rem',
                    borderRadius: '9999px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${primaryColor}20`,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: '0.5rem',
                      height: '0.5rem',
                      borderRadius: '9999px',
                      backgroundColor: primaryColor,
                      display: 'block',
                    }}
                  />
                </span>
                <span>{text}</span>
              </li>
            ))}
          </ul>

          <div
            style={{
              marginTop: '2.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <div style={{ fontSize: '0.95rem', color: '#64748b' }}>
              <span style={{ color: ink, fontWeight: 600 }}>Dr. Reena Liz A. Cube</span>
              <span style={{ color: '#94a3b8' }}> · </span>
              <span>General, Restorative & Cosmetic Dentistry</span>
            </div>

            {(branding.supportEmail || branding.supportPhone) && (
              <div style={{ fontSize: '0.95rem', color: '#64748b' }}>
                Need help?{' '}
                <span style={{ color: ink }}>
                  {[branding.supportEmail, branding.supportPhone].filter(Boolean).join('  •  ')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
          Powered by {branding.poweredBy ?? 'Helios Core'}
        </div>
      </div>
    </div>
  );
}
